import { useResponsiveProp } from '@shopify/restyle';
import { EditButton } from 'app/components/edit-button';
import { Column } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { useImagePickerCamera } from 'app/hooks/use-imagepicker-camera';
import { Placeholder, PlaceholderLine, ShineOverlay } from 'app/overrides/rn-placeholder';
import type { ImagePickerSuccessResult } from 'expo-image-picker';
import type { ComponentProps } from 'react';

import { OpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

interface ThumbnailProps extends ComponentProps<typeof PCImage> {
  onChange?: (result: ImagePickerSuccessResult) => void;
  useOwnCache?: boolean;
  hideEditButton?: boolean;
  imageStyle?: ComponentProps<typeof PCImage>['style'];
  badge?: React.ReactNode;
}

export function Thumbnail({
  height = 100,
  width = height,
  style,
  imageStyle,
  onChange,
  source,
  useOwnCache,
  hideEditButton,
  badge,
  ...rest
}: Readonly<ThumbnailProps>) {
  const { onUseImagePickerCamera, settingsPromptState } = useImagePickerCamera(onChange, undefined, {
    allowsMultipleSelection: false,
  });

  return (
    <>
      <Column {...{ height, style, width }}>
        <PCImage
          aspectRatio={1}
          {...rest}
          style={imageStyle ?? { height, width, borderRadius: height / 2 }}
          useOwnCache={useOwnCache}
          {...{ source }}
        />

        {onChange && !hideEditButton ? (
          <Column position="absolute" right={0} top={0}>
            <EditButton onPress={() => onUseImagePickerCamera()} />
          </Column>
        ) : null}
        {badge}
      </Column>
      <OpenSettingsPrompt
        isOpen={settingsPromptState.isOpen}
        onClose={settingsPromptState.onClose}
        settingName="camera"
      />
    </>
  );
}

Thumbnail.Skeleton = function ThumbnailSkeleton({
  height = 100,
  width = height,
}: Pick<ThumbnailProps, 'height' | 'width'>) {
  const resolvedHeight = useResponsiveProp(height);
  const resolvedWidth = useResponsiveProp(width);

  return (
    <Placeholder Animation={ShineOverlay}>
      <PlaceholderLine
        style={{
          width: resolvedWidth as number,
          height: resolvedHeight as number,
          borderRadius: theme.borderRadii['radius.lg'],
        }}
      />
    </Placeholder>
  );
};
